import {Injectable} from '@angular/core';

/**
 * Renders the markdown subset used by Harmony help articles into HTML.
 *
 * Harmony V3 delegates this to `ngx-markdown`. This application has no markdown dependency and
 * pins `node <= 16`, so adding one is not a safe change here; instead the subset the help package
 * actually uses is rendered directly.
 *
 * Security posture: every input character is HTML-escaped first, and markup is only ever produced
 * by this renderer. Raw HTML embedded in help content is therefore shown literally rather than
 * executed (the single exception is an authored `<br>`), and link/image URLs are restricted to
 * safe schemes. Callers may pass the result to `DomSanitizer.bypassSecurityTrustHtml` because the
 * output is generated here, not supplied by the caller.
 *
 * Supported: ATX headings, bold, italic, strikethrough, inline code, fenced code blocks,
 * unordered/ordered lists, blockquotes, horizontal rules, links, images, hard line breaks.
 */
@Injectable({providedIn: 'root'})
export class HelpMarkdownService {
  private static readonly SAFE_LINK_SCHEME = /^(https?:\/\/|mailto:)/i;
  private static readonly SAFE_IMAGE_SCHEME = /^https?:\/\//i;

  /**
   * Private-use sentinels park code spans while the remaining inline passes run. Help text cannot
   * contain these code points, so a placeholder can never collide with real content.
   */
  private static readonly CODE_OPEN = '\uE000';
  private static readonly CODE_CLOSE = '\uE001';
  private static readonly CODE_PLACEHOLDER = /\uE000(\d+)\uE001/g;

  render(markdown: string): string {
    if (!markdown) {
      return '';
    }

    const lines = markdown.replace(/\r\n?/g, '\n').split('\n');

    const html: string[] = [];
    let paragraph: string[] = [];
    let listType: 'ul' | 'ol' | null = null;
    let inQuote = false;
    let fence: string | null = null;
    let code: string[] = [];

    const closeParagraph = () => {
      if (paragraph.length) {
        html.push('<p>' + this.renderInline(paragraph.join('\n')) + '</p>');
        paragraph = [];
      }
    };

    const closeList = () => {
      if (listType) {
        html.push('</' + listType + '>');
        listType = null;
      }
    };

    const closeQuote = () => {
      if (inQuote) {
        html.push('</blockquote>');
        inQuote = false;
      }
    };

    const closeBlocks = () => {
      closeParagraph();
      closeList();
      closeQuote();
    };

    const flushCode = () => {
      html.push('<pre><code>' + this.escape(code.join('\n')) + '</code></pre>');
      code = [];
    };

    for (const rawLine of lines) {
      const line = rawLine.replace(/\t/g, '    ');

      // Fenced code: content is emitted verbatim (escaped) and never parsed as markdown.
      const fenceMatch = line.match(/^\s*(```|~~~)\s*[A-Za-z0-9_+-]*\s*$/);
      if (fence) {
        if (fenceMatch && fenceMatch[1] === fence) {
          flushCode();
          fence = null;
        } else {
          code.push(rawLine);
        }
        continue;
      }
      if (fenceMatch) {
        closeBlocks();
        fence = fenceMatch[1];
        continue;
      }

      if (!line.trim()) {
        closeBlocks();
        continue;
      }

      const heading = line.match(/^\s{0,3}(#{1,6})\s+(.*)$/);
      if (heading) {
        closeBlocks();
        const level = heading[1].length;
        html.push('<h' + level + '>' + this.renderInline(heading[2].trim()) + '</h' + level + '>');
        continue;
      }

      if (/^\s{0,3}([-*_])(\s*\1){2,}\s*$/.test(line)) {
        closeBlocks();
        html.push('<hr />');
        continue;
      }

      const quote = line.match(/^\s{0,3}>\s?(.*)$/);
      if (quote) {
        closeParagraph();
        closeList();
        if (!inQuote) {
          html.push('<blockquote>');
          inQuote = true;
        }
        html.push('<p>' + this.renderInline(quote[1]) + '</p>');
        continue;
      }
      closeQuote();

      const unordered = line.match(/^\s{0,3}[-*+]\s+(.*)$/);
      if (unordered) {
        closeParagraph();
        if (listType !== 'ul') {
          closeList();
          html.push('<ul>');
          listType = 'ul';
        }
        html.push('<li>' + this.renderInline(unordered[1]) + '</li>');
        continue;
      }

      const ordered = line.match(/^\s{0,3}\d+[.)]\s+(.*)$/);
      if (ordered) {
        closeParagraph();
        if (listType !== 'ol') {
          closeList();
          html.push('<ol>');
          listType = 'ol';
        }
        html.push('<li>' + this.renderInline(ordered[1]) + '</li>');
        continue;
      }

      // An indented continuation line belongs to the open list item, not a new paragraph.
      if (listType && /^\s{2,}/.test(line)) {
        const last = html.length - 1;
        if (html[last].indexOf('<li>') === 0) {
          html[last] = html[last].replace(
            /<\/li>$/,
            ' ' + this.renderInline(line.trim()) + '</li>');
          continue;
        }
      }

      closeList();
      paragraph.push(line.trim());
    }

    if (fence) {
      // Unterminated fence: emit what was collected rather than dropping it.
      flushCode();
    }
    closeBlocks();

    return html.join('\n');
  }

  /**
   * Inline formatting. The text is escaped up front, so every tag below is one this method
   * produced; code spans are parked behind sentinels so later passes cannot reformat them.
   */
  private renderInline(text: string): string {
    const codeSpans: string[] = [];
    let value = this.escape(text);

    value = value.replace(/(`+)([\s\S]+?)\1/g, (match, ticks, content: string) => {
      codeSpans.push('<code>' + content.trim() + '</code>');
      return HelpMarkdownService.CODE_OPEN
        + (codeSpans.length - 1)
        + HelpMarkdownService.CODE_CLOSE;
    });

    // Images before links: the two syntaxes differ only by the leading '!'.
    value = value.replace(
      /!\[([^\]]*)\]\(([^)\s]+)(?:\s+&quot;([^&]*)&quot;)?\)/g,
      (match, alt: string, url: string, title: string) => {
        const safe = this.sanitizeUrl(url, HelpMarkdownService.SAFE_IMAGE_SCHEME);
        if (!safe) {
          return match;
        }
        const titleAttr = title ? ' title="' + title + '"' : '';
        return '<img src="' + safe + '" alt="' + alt + '"' + titleAttr + ' loading="lazy" />';
      });

    value = value.replace(
      /\[([^\]]+)\]\(([^)\s]+)(?:\s+&quot;([^&]*)&quot;)?\)/g,
      (match, label: string, url: string, title: string) => {
        const safe = this.sanitizeUrl(url, HelpMarkdownService.SAFE_LINK_SCHEME);
        if (!safe) {
          return match;
        }
        const titleAttr = title ? ' title="' + title + '"' : '';
        return '<a href="' + safe + '" target="_blank" rel="noopener noreferrer"'
          + titleAttr + '>' + label + '</a>';
      });

    value = value.replace(/(\*\*|__)(?=\S)([\s\S]*?\S)\1/g, '<strong>$2</strong>');
    value = value.replace(/(^|[^*\w])\*(?=\S)([^*]*?\S)\*/g, '$1<em>$2</em>');
    value = value.replace(/(^|[^_\w])_(?=\S)([^_]*?\S)_/g, '$1<em>$2</em>');
    value = value.replace(/~~(?=\S)([\s\S]*?\S)~~/g, '<del>$1</del>');

    // Hard breaks: an authored literal <br> (now escaped), or a markdown two-space line ending.
    value = value.replace(/&lt;br\s*\/?&gt;/gi, '<br />');
    value = value.replace(/ {2,}\n/g, '<br />');
    value = value.replace(/\n/g, ' ');

    return value.replace(
      HelpMarkdownService.CODE_PLACEHOLDER,
      (match, index: string) => codeSpans[+index]);
  }

  /** Returns the URL when its scheme is allowed, otherwise null so the literal text is kept. */
  private sanitizeUrl(url: string, allowed: RegExp): string | null {
    const trimmed = (url || '').trim();
    if (!trimmed) {
      return null;
    }

    // The URL arrives already escaped, so quotes cannot break out of the attribute.
    if (allowed.test(trimmed)) {
      return trimmed;
    }

    // Root-relative links stay inside Harmony and are safe.
    return trimmed.charAt(0) === '/' ? trimmed : null;
  }

  private escape(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
